/**
 * NoteComposer — the add-note form (text + optional "attach to a job" step),
 * extracted from NotesSection so NotesPage can offer note creation without
 * duplicating the job-picker/Tharstern-mirror logic in a second copy.
 *
 * `jobPickerCustomerId`, when set, adds the optional job-attach step: picking
 * a job re-points the new note's connection at that job (subject_type: "job",
 * subject_id: job_no) and sets create_on_tharstern — the only way a note can
 * mirror to Tharstern (see workflow_module's write_note.py). Omit it when the
 * caller is already itself job-scoped — there's nothing to pick.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { IssuePickerJob } from "@shared-core/data/api/issue-pickers";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../primitives/command";

export interface NoteComposerProps {
  subjectType?: string;
  subjectId?: string;
  subjectLabel?: string;
  customerId?: string;
  jobPickerCustomerId?: string;
  /** Called after a note is successfully created, so the caller can refresh
   *  whatever list it owns. */
  onCreated: () => void | Promise<void>;
}

const JOB_SEARCH_DEBOUNCE_MS = 250;
const JOB_RESULTS_PAGE_SIZE = 20;

export function NoteComposer({
  subjectType,
  subjectId,
  subjectLabel,
  customerId,
  jobPickerCustomerId,
  onCreated,
}: NoteComposerProps) {
  const dataAdapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [attachToJob, setAttachToJob] = useState(false);
  const [jobQuery, setJobQuery] = useState("");
  const [jobCandidates, setJobCandidates] = useState<IssuePickerJob[] | null>(
    null,
  );
  const [jobSearchLoading, setJobSearchLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState<IssuePickerJob | null>(null);
  const jobFetchSeq = useRef(0);

  const runJobSearch = useCallback(
    async (q: string) => {
      if (!jobPickerCustomerId) return;
      const seq = ++jobFetchSeq.current;
      setJobSearchLoading(true);
      try {
        const result = await dataAdapter.getJobsForIssuePicker(
          jobPickerCustomerId,
          { q: q || undefined, pageSize: JOB_RESULTS_PAGE_SIZE },
        );
        if (seq !== jobFetchSeq.current) return;
        setJobCandidates(result.items);
      } catch {
        if (seq !== jobFetchSeq.current) return;
        setJobCandidates([]);
      } finally {
        if (seq === jobFetchSeq.current) setJobSearchLoading(false);
      }
    },
    [dataAdapter, jobPickerCustomerId],
  );

  useEffect(() => {
    if (!attachToJob || selectedJob) return;
    const handle = setTimeout(
      () => void runJobSearch(jobQuery.trim()),
      JOB_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runJobSearch is stable per jobPickerCustomerId; selectedJob is read, not depended on, so picking a job doesn't re-trigger a search
  }, [jobQuery, attachToJob]);

  function reset() {
    setNoteText("");
    setOpen(false);
    setAttachToJob(false);
    setJobQuery("");
    setJobCandidates(null);
    setSelectedJob(null);
  }

  async function handleSave() {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      const linkedToJob = attachToJob && selectedJob;
      await dataAdapter.createNote({
        subject_type: linkedToJob ? "job" : subjectType,
        subject_id: linkedToJob ? selectedJob.job_no : subjectId,
        subject_label: linkedToJob ? `Job ${selectedJob.job_no}` : subjectLabel,
        customer_id: customerId,
        note_text: noteText.trim(),
        // Only takes effect for subject_type "job" — see module docstring.
        create_on_tharstern: Boolean(linkedToJob),
      });
      reset();
      await onCreated();
    } catch (err) {
      console.error("note_composer.create_failed", {
        status: err instanceof Error ? err.message : "unknown",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="size-3.5 mr-1" />
        Add note
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
        placeholder="Add a note…"
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        autoFocus
      />
      {jobPickerCustomerId && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={attachToJob}
              onCheckedChange={(checked) => {
                setAttachToJob(checked === true);
                if (!checked) setSelectedJob(null);
              }}
            />
            Attach to a job (also sends this note to Tharstern)
          </label>
          {attachToJob && !selectedJob && (
            <Command
              shouldFilter={false}
              className="rounded-md border border-border"
            >
              <CommandInput
                placeholder="Search by job number or title…"
                value={jobQuery}
                onValueChange={setJobQuery}
              />
              <CommandList className="max-h-[200px]">
                {jobSearchLoading && (
                  <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" />
                    Searching…
                  </div>
                )}
                {!jobSearchLoading &&
                  jobCandidates &&
                  jobCandidates.length === 0 && (
                    <CommandEmpty>No jobs found.</CommandEmpty>
                  )}
                {!jobSearchLoading &&
                  jobCandidates &&
                  jobCandidates.length > 0 && (
                    <CommandGroup>
                      {jobCandidates.map((job) => (
                        <CommandItem
                          key={job.job_no}
                          value={job.job_no}
                          onSelect={() => setSelectedJob(job)}
                        >
                          <span className="font-medium">#{job.job_no}</span>
                          <span className="text-muted-foreground truncate ml-1.5">
                            {job.title}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
              </CommandList>
            </Command>
          )}
          {selectedJob && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm">
              <span>
                #{selectedJob.job_no} — {selectedJob.title}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2"
                onClick={() => setSelectedJob(null)}
              >
                Change
              </Button>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={
            !noteText.trim() || submitting || (attachToJob && !selectedJob)
          }
          onClick={() => void handleSave()}
        >
          {submitting ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
